// Local Bible API Service
// Parses and serves Bible content from local HTML files

import { OSIS_BOOK_NAMES, getBookName, getChapterCount } from '../constants/bibleConstants';

// Cache for loaded translations
const translationCache = {};
const chapterCache = {};

/**
 * Get available translations from the local bibles folder
 */
export async function getLocalTranslations() {
  console.log('localBibleApi: getLocalTranslations() called');
  try {
    // We'll check each known translation folder - NIV first as default, then others
    const knownTranslations = ['eng_niv', 'eng_esv', 'eng_kjv', 'eng_web', 'eng_oeb', 'ara_svd', 'gre_sblgn', 'heb_wlc'];
    console.log('localBibleApi: Checking for translations:', knownTranslations);
    const translations = [];
    
    for (const code of knownTranslations) {
      try {
        // Use full URL path to avoid routing issues
        const baseUrl = window.location.origin;
        const url = `${baseUrl}/bibles/${code}/version.json`;
        console.log(`localBibleApi: Fetching ${url}`);
        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'default',
          mode: 'cors'
        });
        console.log(`localBibleApi: Response for ${code}: ${response.status}`);
        if (response.ok) {
          const data = await response.json();
          console.log(`localBibleApi: Successfully loaded ${code}`);
          translations.push({
            id: code,
            code: data.code || code,
            name: data.name,
            abbreviation: data.abbreviation,
            language: data.language,
            direction: data.direction || 'ltr',
            books: data.books || [],
            bookNames: data.bookNames || [],
            hasLemma: data.hasLemma || false,
            isLocal: true
          });
          translationCache[code] = data;
        } else {
          console.warn(`localBibleApi: Failed to load ${code}, status: ${response.status}`);
        }
      } catch (e) {
        console.error(`localBibleApi: Error loading ${code}:`, e);
      }
    }
    
    console.log(`localBibleApi: Total translations loaded: ${translations.length}`);
    
    // If no translations loaded, provide fallback
    if (translations.length === 0) {
      console.error('localBibleApi: NO TRANSLATIONS LOADED! App will not function correctly.');
      console.error('localBibleApi: This may be due to CORS, file not found, or Safari restrictions.');
    }
    
    return translations;
  } catch (error) {
    console.error('Error getting local translations:', error);
    return [];
  }
}

/**
 * Get books for a specific translation
 */
export function getBooks(translationId) {
  const translation = translationCache[translationId];
  if (!translation) {
    // Return default book list
    return Object.entries(OSIS_BOOK_NAMES).map(([id, name], index) => ({
      id,
      name,
      chapters: getChapterCount(id),
      testament: index < 39 ? 'OT' : 'NT'
    }));
  }
  
  return translation.books.map((bookId, index) => ({
    id: bookId,
    name: translation.bookNames[index] || getBookName(bookId),
    chapters: getChapterCount(bookId),
    testament: index < 39 ? 'OT' : 'NT'
  }));
}

/**
 * Parse HTML content to extract verses
 */
function parseChapterHtml(html, bookId, chapter) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  const verses = [];
  const verseElements = doc.querySelectorAll('span.verse');
  
  verseElements.forEach((verseEl, index) => {
    const osisRef = verseEl.getAttribute('data-osis');
    const verseNum = index + 1;
    
    // Get verse text, removing verse number span
    let text = '';
    const children = verseEl.childNodes;
    children.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child;
        // Skip verse number spans
        if (!el.classList.contains('verse-num')) {
          // For words with Strong's numbers, just get the text
          if (el.classList.contains('w')) {
            text += el.textContent;
          } else {
            text += el.textContent;
          }
        }
      }
    });
    
    // Extract Strong's numbers if present
    const strongs = [];
    const wordElements = verseEl.querySelectorAll('span.w[data-lemma]');
    wordElements.forEach(wordEl => {
      const lemma = wordEl.getAttribute('data-lemma');
      if (lemma) {
        strongs.push({
          word: wordEl.textContent.trim(),
          strongs: lemma
        });
      }
    });
    
    verses.push({
      id: osisRef || `${bookId}.${chapter}.${verseNum}`,
      reference: `${getBookName(bookId)} ${chapter}:${verseNum}`,
      bookId,
      chapter,
      verse: verseNum,
      text: text.trim(),
      strongs: strongs.length > 0 ? strongs : undefined
    });
  });
  
  return verses;
}

// Cache for loaded book data (consolidated JSON files)
const bookDataCache = {};

/**
 * Get chapter content from local Bible files
 * Uses consolidated book JSON files for better performance and fewer requests
 */
export async function getChapter(translationId, bookId, chapter) {
  const cacheKey = `${translationId}:${bookId}:${chapter}`;
  
  if (chapterCache[cacheKey]) {
    return chapterCache[cacheKey];
  }
  
  try {
    const baseUrl = window.location.origin;
    let html;
    
    // First try consolidated book JSON file
    const bookCacheKey = `${translationId}:${bookId}`;
    if (!bookDataCache[bookCacheKey]) {
      try {
        const bookUrl = `${baseUrl}/bibles/${translationId}/books/${bookId}.json`;
        console.log(`Fetching book: ${bookUrl}`);
        
        const bookResponse = await fetch(bookUrl, { 
          credentials: 'same-origin',
          cache: 'default',
          mode: 'cors'
        });
        
        console.log(`Book fetch status: ${bookResponse.status}`);
        
        if (bookResponse.ok) {
          const bookData = await bookResponse.json();
          console.log(`Book data loaded for ${bookId}, chapters:`, Object.keys(bookData.chapters || {}).length);
          bookDataCache[bookCacheKey] = bookData;
        }
      } catch (e) {
        console.warn(`Failed to load consolidated book file for ${bookId}:`, e);
        // Consolidated file not available, will fall back to individual HTML
      }
    }
    
    // Use consolidated data if available
    if (bookDataCache[bookCacheKey]?.chapters?.[chapter]) {
      const chapterData = bookDataCache[bookCacheKey].chapters[chapter];
      console.log(`Using cached chapter data for ${bookId} ${chapter}`);
      
      // Check if this is NIV format (object with verses) or KJV format (HTML string)
      if (typeof chapterData === 'object' && !Array.isArray(chapterData)) {
        // NIV format: { "1": "verse text", "2": "verse text", ... }
        const verses = Object.entries(chapterData).map(([verseNum, text]) => ({
          id: `${bookId}.${chapter}.${verseNum}`,
          reference: `${getBookName(bookId)} ${chapter}:${verseNum}`,
          bookId,
          chapter,
          verse: parseInt(verseNum),
          text: text,
          strongs: undefined
        }));
        
        console.log(`Parsed ${verses.length} verses for ${bookId} ${chapter}`);
        
        const result = {
          translationId,
          bookId,
          bookName: getBookName(bookId),
          chapter,
          verses,
          totalChapters: getChapterCount(bookId)
        };
        
        chapterCache[cacheKey] = result;
        return result;
      } else {
        // KJV format: HTML string
        html = chapterData;
      }
    } else {
      console.log(`No cached data, fetching individual HTML file for ${bookId}.${chapter}`);
      // Fall back to individual HTML file
      const url = `${baseUrl}/bibles/${translationId}/${bookId}.${chapter}.html`;
      const response = await fetch(url, { 
        credentials: 'same-origin',
        cache: 'default',
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Chapter not found: ${bookId} ${chapter} (HTTP ${response.status})`);
      }
      
      html = await response.text();
      
      // Verify we got actual Bible HTML, not the React app's index.html
      if (html.includes('<!DOCTYPE html>') && html.includes('<div id="root">')) {
        throw new Error(`Got React app instead of Bible file for ${bookId}.${chapter}`);
      }
    }
    
    const verses = parseChapterHtml(html, bookId, chapter);
    
    // Validate we actually parsed verses
    if (!verses || verses.length === 0) {
      console.warn(`No verses parsed for ${bookId}.${chapter}`);
    }
    
    const result = {
      translationId,
      bookId,
      bookName: getBookName(bookId),
      chapter,
      verses,
      totalChapters: getChapterCount(bookId)
    };
    
    chapterCache[cacheKey] = result;
    return result;
  } catch (error) {
    console.error(`Error loading chapter ${bookId} ${chapter}:`, error);
    throw error;
  }
}

/**
 * Get a specific verse
 */
export async function getVerse(translationId, bookId, chapter, verse) {
  const chapterData = await getChapter(translationId, bookId, chapter);
  return chapterData.verses.find(v => v.verse === verse) || null;
}

/**
 * Parse a verse reference like "1 John 5:8", "John 3:16", "Gen 1:1"
 * Returns { bookId, chapter, verse } or null if not a valid reference
 */
function parseVerseReference(query) {
  // Common book name mappings
  const bookMappings = {
    'genesis': 'Gen', 'gen': 'Gen',
    'exodus': 'Exod', 'exod': 'Exod', 'ex': 'Exod',
    'leviticus': 'Lev', 'lev': 'Lev',
    'numbers': 'Num', 'num': 'Num',
    'deuteronomy': 'Deut', 'deut': 'Deut', 'deu': 'Deut',
    'joshua': 'Josh', 'josh': 'Josh',
    'judges': 'Judg', 'judg': 'Judg', 'jdg': 'Judg',
    'ruth': 'Ruth',
    '1 samuel': '1Sam', '1samuel': '1Sam', '1sam': '1Sam', '1 sam': '1Sam',
    '2 samuel': '2Sam', '2samuel': '2Sam', '2sam': '2Sam', '2 sam': '2Sam',
    '1 kings': '1Kgs', '1kings': '1Kgs', '1kgs': '1Kgs', '1 kgs': '1Kgs',
    '2 kings': '2Kgs', '2kings': '2Kgs', '2kgs': '2Kgs', '2 kgs': '2Kgs',
    '1 chronicles': '1Chr', '1chronicles': '1Chr', '1chr': '1Chr', '1 chr': '1Chr',
    '2 chronicles': '2Chr', '2chronicles': '2Chr', '2chr': '2Chr', '2 chr': '2Chr',
    'ezra': 'Ezra',
    'nehemiah': 'Neh', 'neh': 'Neh',
    'esther': 'Esth', 'esth': 'Esth',
    'job': 'Job',
    'psalms': 'Ps', 'psalm': 'Ps', 'ps': 'Ps', 'psa': 'Ps',
    'proverbs': 'Prov', 'prov': 'Prov', 'pro': 'Prov',
    'ecclesiastes': 'Eccl', 'eccl': 'Eccl', 'ecc': 'Eccl',
    'song of solomon': 'Song', 'song': 'Song', 'sos': 'Song', 'song of songs': 'Song',
    'isaiah': 'Isa', 'isa': 'Isa',
    'jeremiah': 'Jer', 'jer': 'Jer',
    'lamentations': 'Lam', 'lam': 'Lam',
    'ezekiel': 'Ezek', 'ezek': 'Ezek', 'eze': 'Ezek',
    'daniel': 'Dan', 'dan': 'Dan',
    'hosea': 'Hos', 'hos': 'Hos',
    'joel': 'Joel',
    'amos': 'Amos',
    'obadiah': 'Obad', 'obad': 'Obad',
    'jonah': 'Jonah', 'jon': 'Jonah',
    'micah': 'Mic', 'mic': 'Mic',
    'nahum': 'Nah', 'nah': 'Nah',
    'habakkuk': 'Hab', 'hab': 'Hab',
    'zephaniah': 'Zeph', 'zeph': 'Zeph',
    'haggai': 'Hag', 'hag': 'Hag',
    'zechariah': 'Zech', 'zech': 'Zech',
    'malachi': 'Mal', 'mal': 'Mal',
    'matthew': 'Matt', 'matt': 'Matt', 'mat': 'Matt',
    'mark': 'Mark', 'mrk': 'Mark',
    'luke': 'Luke', 'luk': 'Luke',
    'john': 'John', 'joh': 'John', 'jn': 'John',
    'acts': 'Acts', 'act': 'Acts',
    'romans': 'Rom', 'rom': 'Rom',
    '1 corinthians': '1Cor', '1corinthians': '1Cor', '1cor': '1Cor', '1 cor': '1Cor',
    '2 corinthians': '2Cor', '2corinthians': '2Cor', '2cor': '2Cor', '2 cor': '2Cor',
    'galatians': 'Gal', 'gal': 'Gal',
    'ephesians': 'Eph', 'eph': 'Eph',
    'philippians': 'Phil', 'phil': 'Phil', 'php': 'Phil',
    'colossians': 'Col', 'col': 'Col',
    '1 thessalonians': '1Thess', '1thessalonians': '1Thess', '1thess': '1Thess', '1 thess': '1Thess',
    '2 thessalonians': '2Thess', '2thessalonians': '2Thess', '2thess': '2Thess', '2 thess': '2Thess',
    '1 timothy': '1Tim', '1timothy': '1Tim', '1tim': '1Tim', '1 tim': '1Tim',
    '2 timothy': '2Tim', '2timothy': '2Tim', '2tim': '2Tim', '2 tim': '2Tim',
    'titus': 'Titus', 'tit': 'Titus',
    'philemon': 'Phlm', 'phlm': 'Phlm', 'phm': 'Phlm',
    'hebrews': 'Heb', 'heb': 'Heb',
    'james': 'Jas', 'jas': 'Jas',
    '1 peter': '1Pet', '1peter': '1Pet', '1pet': '1Pet', '1 pet': '1Pet',
    '2 peter': '2Pet', '2peter': '2Pet', '2pet': '2Pet', '2 pet': '2Pet',
    '1 john': '1John', '1john': '1John', '1jn': '1John', '1 jn': '1John',
    '2 john': '2John', '2john': '2John', '2jn': '2John', '2 jn': '2John',
    '3 john': '3John', '3john': '3John', '3jn': '3John', '3 jn': '3John',
    'jude': 'Jude',
    'revelation': 'Rev', 'rev': 'Rev', 'revelations': 'Rev'
  };

  // Pattern: "Book Chapter:Verse" or "Book Chapter" (with optional numbers in book name)
  // Examples: "John 3:16", "1 John 5:8", "Genesis 1", "1 Cor 13:4"
  const refPattern = /^(\d?\s*[a-zA-Z]+(?:\s+of\s+[a-zA-Z]+)?)\s+(\d+)(?::(\d+)(?:-(\d+))?)?$/i;
  const match = query.trim().match(refPattern);
  
  if (!match) return null;
  
  const bookName = match[1].toLowerCase().trim();
  const chapter = parseInt(match[2]);
  const verse = match[3] ? parseInt(match[3]) : null;
  const verseEnd = match[4] ? parseInt(match[4]) : null;
  
  // Look up book ID
  const bookId = bookMappings[bookName];
  if (!bookId) return null;
  
  return { bookId, chapter, verse, verseEnd };
}

/**
 * Search verses using the word index
 */
export async function searchVerses(translationId, query, options = {}) {
  const { limit = 100 } = options;
  const results = [];
  
  try {
    // Normalize query
    const normalizedQuery = query.trim();
    
    if (!normalizedQuery) {
      return results;
    }
    
    // First, check if query is a verse reference (e.g., "1 John 5:8", "John 3:16")
    const verseRef = parseVerseReference(normalizedQuery);
    if (verseRef) {
      try {
        if (verseRef.verse) {
          // Specific verse requested
          const verse = await getVerse(translationId, verseRef.bookId, verseRef.chapter, verseRef.verse);
          if (verse) {
            // If verse range, get all verses in range
            if (verseRef.verseEnd) {
              for (let v = verseRef.verse; v <= verseRef.verseEnd; v++) {
                const rangeVerse = await getVerse(translationId, verseRef.bookId, verseRef.chapter, v);
                if (rangeVerse) results.push(rangeVerse);
              }
            } else {
              results.push(verse);
            }
            return results;
          }
        } else {
          // Whole chapter requested - return first few verses as preview
          const chapterData = await getChapter(translationId, verseRef.bookId, verseRef.chapter);
          if (chapterData && chapterData.verses) {
            return chapterData.verses.slice(0, Math.min(limit, 10));
          }
        }
      } catch (e) {
        console.warn('Verse reference lookup failed, falling back to text search:', e);
      }
    }
    
    // Check if this is a phrase search (contains multiple words)
    const isPhrase = normalizedQuery.includes(' ') && 
                     !normalizedQuery.startsWith('"') && 
                     normalizedQuery.split(/\s+/).length > 1;
    
    // For phrase searches, we want to keep the phrase intact
    const queryLower = normalizedQuery.toLowerCase();
    
    // Split into words, but keep the original phrase for matching
    // Only filter out VERY short words (1-2 chars) that aren't significant
    const words = queryLower
      .replace(/[^a-z\s']/g, '') // Keep letters, spaces, and apostrophes
      .split(/\s+/)
      .filter(w => w.length > 0); // Keep all words, don't filter by length
    
    // For phrase search, identify the "anchor" words (longer, less common)
    // but still search for the complete phrase
    const anchorWords = words.filter(w => w.length > 3);
    const searchWords = anchorWords.length > 0 ? anchorWords : words.filter(w => w.length > 2);
    
    if (searchWords.length === 0 && words.length > 0) {
      // All words are short - use them anyway for phrases like "I am"
      searchWords.push(...words.slice(0, 2));
    }
    
    if (searchWords.length === 0) {
      return results;
    }
    
    // For single word search, use the index approach
    if (words.length === 1 && !isPhrase) {
      const searchWord = words[0];
      try {
        const response = await fetch(`/bibles/${translationId}/index/${searchWord}.json`);
        if (response.ok) {
          const references = await response.json();
          
          // Load each referenced verse
          for (const ref of references.slice(0, limit)) {
            try {
              const [bookId, chapter, verse] = ref.split('.');
              const verseData = await getVerse(translationId, bookId, parseInt(chapter), parseInt(verse));
              if (verseData) {
                results.push(verseData);
              }
            } catch (e) {
              console.warn(`Could not load verse ${ref}:`, e);
            }
          }
          return results;
        }
      } catch (e) {
        console.warn(`Error loading index for: ${searchWord}`, e);
      }
    }
    
    // For multi-word/phrase search, use hybrid approach
    // First, try to find verses using available indexes for anchor words
    const wordIndexes = [];
    const availableWords = [];
    
    for (const word of searchWords) {
      try {
        const response = await fetch(`/bibles/${translationId}/index/${word}.json`);
        if (response.ok) {
          const references = await response.json();
          wordIndexes.push(new Set(references));
          availableWords.push(word);
        }
      } catch (e) {
        // Skip words without indexes
        console.debug(`No index for word: ${word}`);
      }
    }
    
    // If we have at least one word with an index, find intersection
    if (wordIndexes.length > 0) {
      // Start with the smallest set for efficiency
      let commonRefs = Array.from(wordIndexes.reduce((smallest, current) => 
        current.size < smallest.size ? current : smallest
      ));
      
      // Intersect with all other word indexes
      for (const wordIndex of wordIndexes) {
        commonRefs = commonRefs.filter(ref => wordIndex.has(ref));
      }
      
      // Now filter these results to match the full phrase
      const versePromises = commonRefs.slice(0, Math.min(commonRefs.length, limit * 3)).map(async ref => {
        try {
          const [bookId, chapter, verse] = ref.split('.');
          const verseData = await getVerse(translationId, bookId, parseInt(chapter), parseInt(verse));
          return verseData;
        } catch (e) {
          console.warn(`Could not load verse ${ref}:`, e);
          return null;
        }
      });
      
      const verses = (await Promise.all(versePromises)).filter(v => v !== null);
      
      // Prepare phrase for matching - normalize but keep word order
      const phraseToMatch = queryLower.replace(/[^a-z\s']/g, '').replace(/\s+/g, ' ').trim();
      
      for (const verse of verses) {
        if (results.length >= limit) break;
        
        // Get the verse text and normalize it
        const verseText = verse.text
          .replace(/<[^>]*>/g, ' ') // Remove HTML tags
          .replace(/&[^;]+;/g, ' ') // Remove HTML entities
          .toLowerCase()
          .replace(/[^a-z\s']/g, ' ') // Keep letters, spaces, apostrophes
          .replace(/\s+/g, ' ') // Normalize whitespace
          .trim();
        
        // Check if the verse contains the search phrase
        if (verseText.includes(phraseToMatch)) {
          results.push(verse);
        } else if (!isPhrase) {
          // For non-phrase searches, also check if all individual words are present
          const allWordsPresent = words.every(word => verseText.includes(word));
          if (allWordsPresent && results.length < limit) {
            results.push(verse);
          }
        }
      }
    }
    
    return results;
  } catch (error) {
    console.error('Error searching verses:', error);
    return [];
  }
}

/**
 * Get verse of the day (random verse from common encouraging verses)
 */
export async function getVerseOfTheDay(translationId = 'eng_niv') {
  const encouragingVerses = [
    { book: 'John', chapter: 3, verse: 16 },
    { book: 'Ps', chapter: 23, verse: 1 },
    { book: 'Phil', chapter: 4, verse: 13 },
    { book: 'Jer', chapter: 29, verse: 11 },
    { book: 'Prov', chapter: 3, verse: 5 },
    { book: 'Isa', chapter: 41, verse: 10 },
    { book: 'Rom', chapter: 8, verse: 28 },
    { book: 'Josh', chapter: 1, verse: 9 },
    { book: 'Matt', chapter: 11, verse: 28 },
    { book: 'Ps', chapter: 46, verse: 1 }
  ];
  
  // Use date to select verse (changes daily)
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  const verseInfo = encouragingVerses[dayOfYear % encouragingVerses.length];
  
  try {
    return await getVerse(translationId, verseInfo.book, verseInfo.chapter, verseInfo.verse);
  } catch (error) {
    console.error('Error getting verse of the day:', error);
    return null;
  }
}

/**
 * Get Strong's dictionary entry (placeholder for future implementation)
 */
export async function getStrongsDefinition(strongsNumber) {
  // This would need a Strong's dictionary JSON file
  // For now, return a placeholder
  return {
    number: strongsNumber,
    original: strongsNumber.startsWith('H') ? 'Hebrew' : 'Greek',
    definition: `Definition for ${strongsNumber} - Strong's dictionary integration coming soon.`
  };
}

const localBibleApi = {
  getLocalTranslations,
  getBooks,
  getChapter,
  getVerse,
  searchVerses,
  getVerseOfTheDay,
  getStrongsDefinition
};

export default localBibleApi;
